import { supabase } from "./supabase";
import * as db from "./db";
import { erEkteProduksjon } from "./session";
import { fraLagret, type Tekstdok } from "@infoflyt/shared/tekst";
import type { Tekstflate } from "./types";

/**
 * Tekstflater: henting i feltappen.
 *
 * Brief for teknisk crew er den foerste. Den SKRIVES paa nettflata og LESES
 * her — ulikt dagsplanen, som kommer ferdig satt som PDF fra en koordinator.
 *
 * Forskjellen er ikke teknisk pirk. En brief som endrer seg gjennom dagen
 * ville vaert en daarlig PDF: den maatte settes paa nytt for to rettede ord,
 * og den leses i telefonens egen skriftstoerrelse naar den er tekst.
 *
 * OFFLINE FOERST, som dokumentene. `lesLokalt` svarer fra disk uten aa roere
 * nettet; `synkroniser` kjoeres etterpaa.
 */

export async function lesLokalt(produksjonId: string): Promise<Tekstflate[]> {
  await db.openDb(produksjonId);
  return db.lastTekstflater(produksjonId);
}

/**
 * Henter fra Supabase og skriver over det lokale.
 *
 * Returnerer null naar det ikke finnes noen klient eller hentingen feiler.
 * Da staar det lokale, og det er riktig: appen skal kunne brukes uten nett.
 */
export async function synkroniser(produksjonId: string): Promise<Tekstflate[] | null> {
  if (supabase === null || !erEkteProduksjon(produksjonId)) return null;
  const { data, error } = await supabase
    .from("text_page")
    .select("id, slug, title, content, updated_at")
    .eq("production_id", produksjonId);
  if (error !== null) return null;

  await db.openDb(produksjonId);

  /*
   * EN SIDE SOM VENTER I UTBOKSEN ROERES IKKE.
   *
   * Synken bytter hele settet, og uten dette ville den overskrevet en
   * avhuking som ennaa ikke hadde kommet fram — brukeren ville sett haken
   * forsvinne av seg selv, og det er den verste varianten: ingen feil,
   * bare noe som ble borte.
   */
  const venter = new Set((await db.utboksen(produksjonId)).map((r) => r.slug));
  const lokale = venter.size === 0
    ? []
    : (await db.lastTekstflater(produksjonId)).filter((f) => venter.has(f.slug));

  const fraNett: Tekstflate[] = (data ?? [])
    .filter((r) => !venter.has(String(r.slug)))
    .map((r) => ({
      id: String(r.id),
      slug: String(r.slug),
      tittel: String(r.title),
      dok: fraLagret(r.content),
      endretIso: String(r.updated_at),
    }));

  const flater = [...fraNett, ...lokale];
  await db.erstattTekstflater(produksjonId, flater);
  return flater;
}

/**
 * Skriver en endring fra appen.
 *
 * LOKALT FOERST, ALLTID. Man huker av et huskepunkt i en kjeller, og det
 * skal staa der med én gang — ikke vente paa dekning. Sida legges i
 * utboksen, og `send` tar den naar nettet er der.
 */
export async function skriv(
  produksjonId: string, slug: string, dok: Tekstdok,
): Promise<void> {
  await db.openDb(produksjonId);
  await db.skrivTekstflate(produksjonId, slug, dok);
}

/**
 * Toemmer utboksen.
 *
 * SISTE SKRIVING VINNER. To som endrer samme felt samtidig gir at den ene
 * overskriver den andre — det er avgjort, og alternativet (feltvis fletting)
 * er en helt annen maskin. Det som sendes er alltid det som STAAR lokalt,
 * saa ti raske avhukinger blir én sending.
 *
 * Returnerer hvor mange sider som gikk opp.
 */
export async function send(produksjonId: string): Promise<number> {
  if (supabase === null || !navigator.onLine || !erEkteProduksjon(produksjonId)) return 0;
  await db.openDb(produksjonId);
  const koe = await db.utboksen(produksjonId);
  let sendt = 0;
  for (const rad of koe) {
    const { error } = await supabase
      .from("text_page")
      .upsert(
        {
          production_id: produksjonId,
          slug: rad.slug,
          title: rad.tittel,
          content: JSON.parse(rad.innhold) as unknown,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "production_id,slug" },
      );
    // Feiler én, staar den i koeen. Vi gaar videre: en annen side skal ikke
    // bli staaende fordi denne ikke kom fram.
    if (error === null) {
      await db.utboksKvitter(produksjonId, rad.slug);
      sendt += 1;
    }
  }
  return sendt;
}
